import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface ProductDetailsSpecificationStyles {
    wrapper?: InjectableCss;
    specificationTittle?: InjectableCss;
    tablediv?: InjectableCss;
    dataRowCss?: InjectableCss;
    descriptionAccordion?: InjectableCss;
    tableContainer?: InjectableCss;
    firstTableDiv?: InjectableCss;
    tableBody?: InjectableCss;
    specificationLable?: InjectableCss;
    attributeValues?: InjectableCss;
}

export const attributesStyles: ProductDetailsSpecificationStyles = {
    wrapper: {
        css: css`
            margin: 20px 0px;
        `,
    },
    specificationTittle: {
        css: css`
            border-bottom: 0.2rem solid ${getColor("primary")};
            box-sizing: border-box;
            display: inline-flex;
            font-weight: 600;
            color: "text.main";
            font-size: 24px;
            text-transform: uppercase;
        `,
    },
    tablediv: {
        css: css`
            border-collapse: collapse;
            border-spacing: 0;
            width: 100%;
        `,
    },
    dataRowCss: {
        css: css`
            width: 100%;
            height: auto;
            display: flex;
            border: 1px solid ${WhitecapColors.tableGrey};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
            :nth-child(even) {
                background-color: ${getColor("primary.contrast")} !important;
            }
        `,
    },
    descriptionAccordion: {
        css: css`
            margin-bottom: 20px;
            border-top: 1px solid  ${getColor("common.border")};
        `,
    },
    tableContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-left: 0;
            margin-right: 0;
        `,
    },
    firstTableDiv: {
        css: css`
            flex: 0 0 50%;
            max-width: 50%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tableBody: {
        css: css`
            border: 0 solid;
            border-color: inherit;
            display: flex;
            flex-wrap: wrap;
            flex-direction: column;
        `,
    },
    specificationLable: {
        css: css`
            width: 50%;
            padding: 5px;
            background: ${getColor("common.border")};
            font-weight: 700;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 40%;
                    `,
                    css`
                        width: 40%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    attributeValues: {
        css: css`
            width: 50%;
            color: ${getColor("text.main")};
            padding: 0px 10px;
            white-space: normal;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 60%;
                    `,
                    css`
                        width: 60%;
                    `,
                    null,
                    null,
                    null,
            ])}
        `,
    },
};

const styles = attributesStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasParentProductId & HasProduct;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
    language: state.context.session.language?.languageCode,
});

const ProductDetailsSpecification: React.FC<Props> = ({ product, variantProductState, fields, language }) => {
    if (!product || !product.properties) {
        return null;
    }

    const [specification, setSpecification] = React.useState([]);

    React.useEffect(() => {
        const isFrench = language === "fr-CA";

        const attributes = isFrench
            ? product?.properties?.frSkuAdditionalAttributes 
                || product?.properties?.skU_ADDITIONAL_ATTRIBUTES
                || variantProductState?.value?.properties?.skU_ADDITIONAL_ATTRIBUTES
            : product?.properties?.skU_ADDITIONAL_ATTRIBUTES
                || variantProductState?.value?.properties?.skU_ADDITIONAL_ATTRIBUTES;

        if (attributes) {
            try {
                setSpecification(JSON.parse(attributes));
            } catch (e) {
                setSpecification([]);
            }
        } else {
            setSpecification([]);
        }
    }, [product, variantProductState, language]);

    if (!specification.length) return null;

    const middleIndex = Math.ceil(specification.length / 2);
    const leftColumn = specification.slice(0, middleIndex);
    const rightColumn = specification.slice(middleIndex);

    return (
        <>
            {specification.length > 0 && (
                <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_specification">
                    <StyledWrapper {...styles.descriptionAccordion}>
                        <StyledWrapper>
                            <Typography {...styles.specificationTittle}>
                                {fields.title ? fields.title : translate("SPECIFICATIONS")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.tableContainer}>
                            <StyledWrapper {...styles.firstTableDiv}>
                                <DataTable {...styles.tablediv}>
                                    <DataTableBody {...styles.tableBody}>
                                        {leftColumn?.map((specification: any, index: number) => (
                                            <DataTableRow {...styles.dataRowCss} key={index}>
                                                <Typography {...styles.specificationLable}>
                                                    {specification.AttributeDisplayName}
                                                </Typography>
                                                <Typography {...styles.attributeValues}>
                                                    {specification.AttributeValue}
                                                </Typography>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                            <StyledWrapper {...styles.firstTableDiv}>
                                <DataTable {...styles.tablediv}>
                                    <DataTableBody {...styles.tableBody}>
                                        {rightColumn?.map((specification: any, index: number) => (
                                            <DataTableRow {...styles.dataRowCss} key={index}>
                                                <Typography {...styles.specificationLable}>
                                                    {specification.AttributeDisplayName}
                                                </Typography>
                                                <Typography {...styles.attributeValues}>
                                                    {specification.AttributeValue}
                                                </Typography>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsSpecification)),
    definition: {
        displayName: "Custom Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title ",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
